const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="headline text-2xl mb-3">{title}</h2>
    <div className="text-sm text-white/60 leading-[1.9] space-y-3">{children}</div>
  </section>
);

const Key = ({ q, children }) => (
  <div className="panel rounded-2xl p-5">
    <div className="display text-[11px] tracking-[0.18em] text-ember mb-2">{q}</div>
    <p className="text-[13px] text-white/60 leading-relaxed">{children}</p>
  </div>
);

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="min-h-screen pt-28 pb-24">
      <div className="mx-auto w-[min(780px,calc(100%-32px))]">
        <div className="eyebrow-label mb-4">Legal</div>
        <h1 className="headline text-4xl sm:text-5xl mb-3">
          <span className="text-white">Privacy </span>
          <span className="text-forge">Policy</span>
        </h1>
        <p className="text-xs text-white/35 mb-12">Last updated 30 March 2026</p>

        <Section title="Who this covers">
          <p>
            This Privacy Notice for <strong className="text-white">The Forge Dice</strong> ("we",
            "us", or "our") describes how and why we might access, collect, store, use, and/or share
            ("process") your personal information when you use our services ("Services"), including
            when you:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              Download and use our mobile application The Forge Dice, or any other application of
              ours that links to this Privacy Notice.
            </li>
            <li>
              Use <strong className="text-white">THE FORGE: Dice Workout Trainer</strong> — turn
              workouts into a game. Roll the dice. Train with purpose.
            </li>
            <li>Visit this website and take part in the community.</li>
            <li>Engage with us in other related ways, including any marketing or events.</li>
          </ul>
          <p>
            <strong className="text-white">Questions or concerns?</strong> Reading this Privacy
            Notice will help you understand your privacy rights and choices. We are responsible for
            making decisions about how your personal information is processed. If you do not agree
            with our policies and practices, please do not use our Services. If you still have any
            questions or concerns, please contact us at{" "}
            <a
              href="mailto:theforgedice@gmail.com"
              className="text-ember hover:text-forge transition-colors"
              data-testid="privacy-contact-email"
            >
              theforgedice@gmail.com
            </a>
            .
          </p>
        </Section>

        <Section title="Summary of key points">
          <div className="grid sm:grid-cols-2 gap-3 not-prose" data-testid="privacy-key-points">
            <Key q="What personal information do we process?">
              When you visit, use, or navigate our Services, we may process personal information
              depending on how you interact with us, the choices you make, and the products and
              features you use.
            </Key>
            <Key q="Do we process sensitive personal information?">
              No. We do not process information considered "special" or "sensitive" in any
              jurisdiction, such as racial or ethnic origins, sexual orientation or religious
              beliefs.
            </Key>
            <Key q="Do we collect information from third parties?">
              No. We do not collect any information from third parties.
            </Key>
            <Key q="How do we process your information?">
              To provide, improve and administer our Services, to communicate with you, for security
              and fraud prevention, and to comply with law — and only where we have a valid legal
              reason to do so.
            </Key>
            <Key q="Who do we share information with?">
              We may share information only in specific situations and with specific third parties
              needed to run the Services.
            </Key>
            <Key q="How do we keep your information safe?">
              We have organisational and technical processes in place to protect your personal
              information, though no transmission or storage technology can be guaranteed to be 100%
              secure.
            </Key>
          </div>
        </Section>

        <Section title="The app">
          <p>
            Your dice, Pouches, plans, workout history, XP and streaks are stored on your device. You
            do not need an account to use the core app, and we do not sell your data.
          </p>
          <p>
            The app stores may share anonymous, aggregated install and crash statistics with us. We
            use these only to fix problems and decide what to build next.
          </p>
        </Section>

        <Section title="This website and community">
          <p>
            You can read the site and the community without an account. If you create an account you
            give us either an email address and password, or — if you sign in with Google — your
            name, email address and profile picture.
          </p>
          <p>
            Anything you post in the community (creations, challenges, feedback, comments, images and
            your display name) is public and visible to anyone visiting the site. Passwords are
            stored as one-way hashes and sign-in uses secure, http-only session cookies.
          </p>
          <p>
            We send you an email only when it relates to something you did — for example when
            someone replies to your post. We do not send marketing blasts.
          </p>
        </Section>

        <Section title="Cookies">
          <p>
            We set a single sign-in cookie so you stay logged in. We do not use advertising or
            tracking cookies.
          </p>
        </Section>

        <Section title="Your rights and choices">
          <p>
            Depending on where you are located, applicable privacy law may give you rights over your
            personal information, including access, correction and deletion. You can delete any post
            or comment you made at any time, and you can ask us to delete your account and
            everything attached to it.
          </p>
        </Section>

        <Section title="Children">
          <p>
            The Forge is not intended for children under 13. If you believe a child has created an
            account, contact us and we will remove it.
          </p>
        </Section>

        <Section title="Health">
          <p>
            The Forge is a training tool, not medical advice. Train within your ability and talk to a
            professional before starting any new programme.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about this notice or your data? Email{" "}
            <a href="mailto:theforgedice@gmail.com" className="text-ember hover:text-forge transition-colors">
              theforgedice@gmail.com
            </a>{" "}
            or post on the Feedback board in the community.
          </p>
        </Section>
      </div>
    </main>
  );
}
